import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const TOOLTIP_GAP = 8;
const VIEWPORT_MARGIN = 12;
const TOOLTIP_MAX_WIDTH = 256;

export default function InfoHelp({ label, title, children, tone = "default" }) {
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [position, setPosition] = useState({
    top: 0,
    left: 0,
    placement: "below",
    ready: false,
  });
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const tooltipRef = useRef(null);
  const tooltipId = `lz-product-info-help-${useId().replace(/:/g, "")}`;
  const open = !dismissed && (hovered || focused || pinned);

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    const tooltip = tooltipRef.current;
    if (!trigger || !tooltip) {
      return;
    }

    const triggerRect = trigger.getBoundingClientRect();
    const tooltipRect = tooltip.getBoundingClientRect();
    const availableWidth = Math.max(0, window.innerWidth - (VIEWPORT_MARGIN * 2));
    const tooltipWidth = tooltipRect.width || Math.min(TOOLTIP_MAX_WIDTH, availableWidth);
    const tooltipHeight = tooltipRect.height;
    const centeredLeft = triggerRect.left + (triggerRect.width / 2) - (tooltipWidth / 2);
    const maximumLeft = Math.max(VIEWPORT_MARGIN, window.innerWidth - tooltipWidth - VIEWPORT_MARGIN);
    const left = Math.min(maximumLeft, Math.max(VIEWPORT_MARGIN, centeredLeft));
    const belowTop = triggerRect.bottom + TOOLTIP_GAP;
    const placeAbove = tooltipHeight > 0 &&
      belowTop + tooltipHeight + VIEWPORT_MARGIN > window.innerHeight;
    const unclampedTop = placeAbove
      ? triggerRect.top - TOOLTIP_GAP - tooltipHeight
      : belowTop;
    const maximumTop = Math.max(VIEWPORT_MARGIN, window.innerHeight - tooltipHeight - VIEWPORT_MARGIN);
    const top = Math.min(maximumTop, Math.max(VIEWPORT_MARGIN, unclampedTop));

    setPosition({
      top,
      left,
      placement: placeAbove ? "above" : "below",
      ready: true,
    });
  }, []);

  useLayoutEffect(() => {
    if (open) {
      updatePosition();
    }
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    function dismiss() {
      setPinned(false);
      setDismissed(true);
    }

    function handlePointerDown(event) {
      if (!rootRef.current?.contains(event.target)) {
        dismiss();
      }
    }

    function handleKeyDown(event) {
      if (event.key === "Escape") {
        event.preventDefault();
        dismiss();
      }
    }

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  return (
    <>
    <span
      ref={rootRef}
      className="lz-product-info-help__root"
      onMouseEnter={() => {
        setHovered(true);
        setDismissed(false);
      }}
      onMouseLeave={() => setHovered(false)}
    >
      <button
        ref={triggerRef}
        type="button"
        className={`lz-product-info-help lz-product-info-help--${tone}`}
        aria-label={`About ${label}`}
        aria-describedby={open ? tooltipId : undefined}
        aria-expanded={open}
        onFocus={() => {
          setFocused(true);
          setDismissed(false);
        }}
        onBlur={() => setFocused(false)}
        onClick={() => {
          if (pinned) {
            setPinned(false);
            setDismissed(true);
          } else {
            setPinned(true);
            setDismissed(false);
          }
        }}
      >
        <svg viewBox="0 0 20 20" aria-hidden="true">
          <circle cx="10" cy="10" r="7.25" />
          <path d="M10 8.75v4.5" />
          <circle cx="10" cy="6.25" r="0.6" fill="currentColor" stroke="none" />
        </svg>
      </button>
    </span>
    {open && typeof document !== "undefined" ? createPortal(
      <span className="lz-v2 lz-product-info-help__portal">
        <span
          ref={tooltipRef}
          id={tooltipId}
          role="tooltip"
          className="lz-product-info-help__tooltip"
          data-placement={position.placement}
          style={{
            top: `${position.top}px`,
            left: `${position.left}px`,
            visibility: position.ready ? "visible" : "hidden",
          }}
        >
          <strong>{title}</strong>
          <span>{children}</span>
        </span>
      </span>,
      document.body
    ) : null}
    </>
  );
}
